import { useEffect, useRef, useState } from 'react'
import { scaleBand } from 'd3-scale'
import { Lock } from 'lucide-react'
import { PremiumGate, upgradeLabel } from '../../platform/access/PremiumGate'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Granularity } from './cashflowPeriod'

/*
 * The free view's two locked panels — the chart and "Where it went" — and its
 * one upgrade prompt
 * (`.agent/reports/m3-shape-locked-preview.md`, M3-R15–R17, plan P2).
 *
 * The previews are PICTURES: no figure, no text, no control, no data read
 * (PremiumGate's preview contract). Their shapes are constants declared inside
 * the components, never at module scope, so nothing fixture-like survives into
 * a bundle as data (memory: an eager map ships a probe fixture). They reuse the
 * real chart's classes, so they inherit its skins in both themes (rule 2).
 */

/** Upgrade has no destination yet: M13 billing owns it (the Accounts and Categories precedent). */
const noDestinationYet = () => {
  /* M13 billing owns the destination; nothing here yet. */
}

/**
 * The single upgrade prompt, after the tiles. Outline, not primary: the Net
 * HeroTile already holds this content area's lime (rule 9). Its accessible name
 * comes from PremiumGate's `upgradeLabel`, the same table the gates read, so the
 * two cannot drift.
 */
export function UpgradePrompt() {
  const phone = useBreakpoint() === 'phone'
  return (
    <Panel className={`cf-upgrade${phone ? ' cf-upgrade--stacked' : ''}`}>
      <span className="cf-upgrade__icon">
        <Icon glyph={Lock} size={16} />
      </span>
      <div className="cf-upgrade__text">
        <h2 className="cf-upgrade__title">{"See what's behind these totals"}</h2>
        <p className="note cf-upgrade__body">
          Premium shows income and spending for every period, which categories and merchants the money went to, where
          your income comes from, and how long your cash would last.
        </p>
      </div>
      <span className="cf-upgrade__action">
        <Button
          variant="outline"
          size="panel"
          className={phone ? 'cf-block-action' : undefined}
          aria-label={upgradeLabel('cashflow_details')}
          onClick={noDestinationYet}
        >
          Upgrade
        </Button>
      </span>
    </Panel>
  )
}

/**
 * Twelve bars, two of them deficits, in the real chart's anatomy. Fits the
 * panel; never scrolls. Drawn at real px: `scaleBand` over the MEASURED width,
 * so one user unit is one CSS pixel and a 24px stub is 24px on a phone as on a
 * wide screen. It was a fixed 1200-wide viewBox stretched with
 * `preserveAspectRatio="none"`, which squeezed the picture about 4× on a phone.
 * Until the first measure (and where nothing measures) it falls back to that
 * stretch; a browser measures before the first paint.
 */
export function ChartPreview() {
  const phone = useBreakpoint() === 'phone'
  const ref = useRef<SVGSVGElement>(null)
  const [measured, setMeasured] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setMeasured(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  // Fractions of the half-plot, shaped after the render's months (Aug → Jul).
  const income = [0.7, 0.78, 0.68, 0.84, 0.7, 0.76, 0.66, 0.7, 0.62, 0.8, 0.74, 0.88]
  const expense = [0.52, 0.58, 0.46, 0.64, 0.96, 0.52, 0.44, 1, 0.6, 0.34, 0.52, 0.44]
  const deficits = new Set([4, 7])
  const W = measured > 0 ? measured : 1200
  // The real chart's axis column: 60px, 48px on a phone.
  const AXIS = phone ? 48 : 60
  const HALF = 100
  const LABEL_Y = 2 * HALF + 12
  const x = scaleBand<number>()
    .domain(income.map((_, i) => i))
    .range([AXIS, W])
    .paddingInner(0.08)
  const bw = x.bandwidth()
  return (
    <svg
      ref={ref}
      className="cf-preview"
      viewBox={`0 0 ${W} ${LABEL_Y + 8}`}
      preserveAspectRatio={measured > 0 ? undefined : 'none'}
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={`axis-${i}`} className="cf-preview__stub" x={Math.max(0, AXIS - 40)} y={Math.min(Math.max(i * (HALF / 2) - 2, 0), 2 * HALF - 4)} width={i === 2 ? 18 : 28} height={4} />
      ))}
      {income.map((h, i) => {
        const left = x(i) ?? 0
        const down = expense[i] ?? 0
        return (
          <g key={`slot-${i}`}>
            <rect className="cf-bar--income" x={left} y={HALF - h * HALF} width={bw} height={h * HALF} />
            {/* Flat --neg-fill, no hatch: the hatch arrives through --cf-hatch, which
                DivergingBars sets on its svg with a <pattern> of its own, and setting
                it here would need an inline style (rule 2). Under blur(3px) the
                stripes would not read anyway; the preview's job is the shape. */}
            <rect
              className={`cf-bar--expense${deficits.has(i) ? ' cf-bar--deficit' : ''}`}
              x={left}
              y={HALF}
              width={bw}
              height={down * HALF}
            />
            <rect className="cf-preview__stub" x={left + bw / 2 - 12} y={LABEL_Y} width={24} height={4} />
          </g>
        )
      })}
      <rect className="cf-preview__zero" x={AXIS} y={HALF - 0.5} width={W - AXIS} height={1} />
    </svg>
  )
}

/** Six ranked rows: a neutral share bar behind a name stub, and an amount stub. */
function RankPreview() {
  const rows = [1, 2, 3, 4, 5, 6]
  return (
    <div className="cf-rank-preview">
      {rows.map((n) => (
        <div key={`row-${n}`} className="cf-rank-preview__row">
          <span className={`cf-rank-preview__bar cf-rank-preview__bar--${n}`}>
            <span className="cf-rank-preview__name" />
          </span>
          <span className="cf-rank-preview__amount" />
        </div>
      ))}
    </div>
  )
}

const BY: Record<Granularity, string> = { month: 'month', quarter: 'quarter', year: 'year' }

/**
 * The locked chart, where the real one would stand: between the period header
 * and the tiles. Its heading sits outside the blur (a title is not premium
 * data, and it names the shape below it). Gates carry no button of their own:
 * `UpgradePrompt` is the one Upgrade on the page; each gate draws its own
 * "Locked" badge instead (`cta="none"`).
 */
export function LockedChart({ granularity }: { granularity: Granularity }) {
  return (
    <Panel className="cf-panel">
      <div className="cf-panel__head">
        <h2 className="cf-panel__title">Income and expense by {BY[granularity]}</h2>
        <p className="cf-panel__sub">Income above the line, spending below it — the gap is what you kept</p>
      </div>
      <PremiumGate entitlement="cashflow_details" cta="none" panelKind="chart" preview={<ChartPreview />}>
        {null}
      </PremiumGate>
    </Panel>
  )
}

/** The locked "Where it went", after the upgrade prompt: the ranked list's shape, no figure in it. */
export function LockedWhereItWent() {
  return (
    <Panel className="cf-panel">
      <div className="cf-panel__head">
        <h2 className="cf-panel__title">Where it went</h2>
        <p className="cf-panel__sub">By category, group or merchant, against the period before</p>
      </div>
      <PremiumGate entitlement="cashflow_details" cta="none" panelKind="categories" preview={<RankPreview />}>
        {null}
      </PremiumGate>
    </Panel>
  )
}
